import { AgChartInstance, AgChartOptions } from "ag-charts-community";
import { forwardRef, useImperativeHandle, useRef } from "react";

import AnalyticsChart from "./AnalyticsChart";
import AnalyticsEmptyState from "./AnalyticsEmptyState";
import AnalyticsLoadingState from "./AnalyticsLoadingState";
import {
  CHART_CARD_HEIGHT_CLASS,
  CHART_DOWNLOAD_FILE_FORMAT,
  DEFAULT_CHART_DOWNLOAD_FILENAME,
} from "./constants";

import { cn } from "@/lib/utils";

export interface AnalyticsChartFrameHandle {
  downloadChart: (fileName?: string) => void;
}

const AnalyticsChartFrame = forwardRef<
  AnalyticsChartFrameHandle,
  {
    options: AgChartOptions;
    resetKey?: string;
    isLoading: boolean;
    isEmpty: boolean;
    loadingLabel: string;
    className?: string;
  }
>(({ options, resetKey, isLoading, isEmpty, loadingLabel, className = "" }, ref) => {
  const chartRef = useRef<AgChartInstance<AgChartOptions>>(null);
  const shouldRenderChart = !isLoading;

  useImperativeHandle(
    ref,
    () => ({
      downloadChart: (fileName = DEFAULT_CHART_DOWNLOAD_FILENAME) => {
        const chart = chartRef.current;
        if (!chart) {
          return;
        }

        void chart.download({
          fileName,
          fileFormat: CHART_DOWNLOAD_FILE_FORMAT,
        });
      },
    }),
    [],
  );

  return (
    <div className={cn("relative w-full", CHART_CARD_HEIGHT_CLASS, className)}>
      <div
        className={cn(
          "h-full w-full overflow-hidden rounded-lg bg-white transition-[filter] duration-200 ease-out",
          (isLoading || isEmpty) && "blur-sm",
        )}
      >
        {shouldRenderChart ? (
          <AnalyticsChart key={resetKey} ref={chartRef} options={options} />
        ) : null}
      </div>

      <div
        className={`pointer-events-none absolute inset-0 flex items-center justify-center bg-white/95 transition-opacity duration-200 ease-out ${
          isLoading ? "opacity-100" : "opacity-0"
        }`}
        aria-hidden={!isLoading}
      >
        <div className="transition-transform duration-200 ease-out">
          <AnalyticsLoadingState label={loadingLabel} />
        </div>
      </div>

      {isEmpty && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/95">
          <AnalyticsEmptyState />
        </div>
      )}
    </div>
  );
});

AnalyticsChartFrame.displayName = "AnalyticsChartFrame";

export default AnalyticsChartFrame;
